import { env } from '../../config';
import { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import dropdownOptions from './dropdownOptions.json';

const PreviewModal = ({state, setShowPreviewModal}) => {
    const containerRef = useRef(null);
    const iframeRef = useRef(null);
    const wrapperRef = useRef(null);
    const controlsRef = useRef(null);

    // Initial state based on 4k landscape
    const [displayConfig, setDisplayConfig] = useState({ w: 3840, h: 2160, type: '4k' });
    const [debouncedConfig, setDebouncedConfig] = useState(displayConfig);

    // --- DEBOUNCE LOGIC ---
    // Updates the calculation state 300ms after the user stops typing
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedConfig(displayConfig);
        }, 300);

        return () => clearTimeout(handler);
    }, [displayConfig]);

    const handleBackgroundClick = (e) => {
        // If the user clicked exactly the PreviewModal (the dark background), close it
        if (e.target === containerRef.current) {
            setShowPreviewModal(false);
        }
    };

    // Prevent clicks inside the controls or wrapper from closing the modal
    const handleContentClick = (e) => {
        e.stopPropagation();
    };

    const handleCustomChange = (e) => {
        const { name, value } = e.target;
        // Only allow numbers, default to 0 if empty
        const val = value === '' ? 0 : parseInt(value, 10);

        setDisplayConfig(prev => ({
            ...prev,
            type: 'custom',
            [name]: val
        }));
    };

    const rotation = useMemo(() => {
        let portrait = [];
        let landscape = [];
        dropdownOptions.SCREEN_ORIENTATION.forEach((orientation) => {
            if (orientation.label.toLowerCase().includes('landscape')) {
                landscape.push(orientation.value);
            } else {
                portrait.push(orientation.value);
            }
        })

        return {portrait: portrait, landscape: landscape};
    }, []);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const iframe = iframeRef.current;
        const wrapper = wrapperRef.current;
        const controls = controlsRef.current;

        if (container && iframe && wrapper && debouncedConfig.w > 0 && debouncedConfig.h > 0) {
            // Determine "Active" Dimensions based on orientation
            let activeW, activeH;
            if (rotation.portrait.includes(state?.playlist?.rotation)) {
                // If portrait, make sure height is the larger dimension
                activeW = Math.min(debouncedConfig.w, debouncedConfig.h);
                activeH = Math.max(debouncedConfig.w, debouncedConfig.h);
            } else {
                // If landscape, make sure Width is the larger dimension
                activeW = Math.max(debouncedConfig.w, debouncedConfig.h);
                activeH = Math.min(debouncedConfig.w, debouncedConfig.h);
            }

            // Measure available space minus the controls height
            const paddingBuffer = 10; // 5px all around
            const controlsHeight = controls?.offsetHeight || 60;

            // Get available space in the modal (minus gap from controls and paddingBuffer)
            const availableWidth = container.clientWidth - (5 * 2) - paddingBuffer;
            const availableHeight = container.clientHeight - controlsHeight - (5 * 2) - paddingBuffer;

            // Calculate best fit for the aspect ratio
            let finalW, finalH;
            const aspectRatio = activeW / activeH;
            if (availableWidth / availableHeight > aspectRatio) {
                finalH = availableHeight;
                finalW = availableHeight * aspectRatio;
            } else {
                finalW = availableWidth;
                finalH = availableWidth / aspectRatio;
            }

            // Update the wrapper box
            wrapper.style.width = `${finalW}px`;
            wrapper.style.height = `${finalH}px`;

            // Update the iframe dimensions
            iframe.style.width = `${activeW}px`;
            iframe.style.height = `${activeH}px`;

            // Scale the iframe from its native resolution to the final size
            const scale = finalW / activeW;
            iframe.style.transform = `scale(${scale})`;
        }
    }, [debouncedConfig]);

    useEffect(() => {
        const handleEsc = (event) => {
            if (event.key === 'Escape') setShowPreviewModal(false);
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [setShowPreviewModal]);

    return <div className="PreviewModal" ref={containerRef} onClick={handleBackgroundClick}>
        <div className="controls" ref={controlsRef} onClick={handleContentClick}>
            <div className="res-group">
                <label>
                    <input
                        type="radio"
                        name="res"
                        checked={displayConfig.type === '1080'}
                        onChange={() => setDisplayConfig({ w: 1920, h: 1080, type: '1080' })}
                    /> 1080p
                </label>
                <label>
                    <input
                        type="radio"
                        name="res"
                        checked={displayConfig.type === '4k'}
                        onChange={() => setDisplayConfig({ w: 3840, h: 2160, type: '4k' })}
                    /> 4k
                </label>
            </div>

            <div className="custom-input-group">
                <label>
                    <input
                        type="radio"
                        name="res"
                        checked={displayConfig.type === 'custom'}
                        onChange={() => setDisplayConfig(prev => ({ ...prev, type: 'custom' }))}
                    /> Custom:
                </label>
                Height
                <input
                    type="number"
                    name="h"
                    value={displayConfig.h}
                    onChange={handleCustomChange}
                    placeholder="Height"
                    disabled={displayConfig.type !== 'custom'}
                />
                x
                Width
                <input
                    type="number"
                    name="w"
                    value={displayConfig.w}
                    onChange={handleCustomChange}
                    placeholder="Width"
                    disabled={displayConfig.type !== 'custom'}
                />
            </div>
            <button className="close-preview" onClick={() => setShowPreviewModal(false)}>Close Preview</button>
        </div>
        <div className="iframe-wrapper" ref={wrapperRef}>
            <iframe
                ref={iframeRef}
                src={`${env.LOADER_URL}/${state?.playlist?.shortCode}?loaderRotation=0`}
                title="CityMotion Preview"
                className="viewport-preview"
                allowFullScreen
            />
        </div>
    </div>
}

export default PreviewModal;
